/**
 * PWA 설치 유도 카드.
 *
 * `beforeinstallprompt` 이벤트가 발생한 환경(Chrome/Edge 등)에서만 노출.
 * 이미 설치되었거나 미지원(iOS Safari 일부)인 경우 자동 숨김.
 *
 * 영구지침: docs/develop/pwa/pwa-spec.md §4
 */
import { useEffect, useState } from 'react'
import { Smartphone, Download, X } from 'lucide-react'
import { safeLocalStorage } from '@/lib/safeStorage'

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

const DISMISS_KEY = 'cb_pwa_install_dismissed_at'
const DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000 // 일주일

export default function PwaInstallCard() {
  const [event, setEvent] = useState<BeforeInstallPromptEvent | null>(null)
  const [dismissed, setDismissed] = useState(false)

  useEffect(() => {
    // 일주일 내 닫은 적 있으면 노출 안 함
    const ts = safeLocalStorage.getItem(DISMISS_KEY)
    if (ts && Date.now() - Number(ts) < DISMISS_TTL_MS) {
      setDismissed(true)
      return
    }

    const handler = (e: Event) => {
      e.preventDefault()
      setEvent(e as BeforeInstallPromptEvent)
    }
    window.addEventListener('beforeinstallprompt', handler)
    return () => window.removeEventListener('beforeinstallprompt', handler)
  }, [])

  if (dismissed || !event) return null

  const onInstall = async () => {
    await event.prompt()
    const { outcome } = await event.userChoice
    if (outcome === 'dismissed') {
      safeLocalStorage.setItem(DISMISS_KEY, String(Date.now()))
    }
    setEvent(null)
  }

  const onClose = () => {
    safeLocalStorage.setItem(DISMISS_KEY, String(Date.now()))
    setDismissed(true)
  }

  return (
    <div className="relative flex items-center gap-4 rounded-2xl border border-cb-gray-100 bg-white p-5">
      <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-cb-primary-light">
        <Smartphone size={22} className="text-cb-primary" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-bold text-cb-gray-900">
          홈 화면에 추가하고 더 빠르게 이용하세요
        </p>
        <p className="mt-0.5 text-xs text-cb-gray-500">
          앱처럼 한 번에 실행 · 알림 받기 · 오프라인 보기
        </p>
      </div>
      <button
        onClick={onInstall}
        className="flex flex-shrink-0 items-center gap-1.5 rounded-xl bg-cb-primary px-4 py-2 text-sm font-bold text-white hover:bg-cb-primary-hover"
      >
        <Download size={14} />
        설치
      </button>
      <button
        onClick={onClose}
        aria-label="닫기"
        className="absolute right-3 top-3 text-cb-gray-300 hover:text-cb-gray-500"
      >
        <X size={14} />
      </button>
    </div>
  )
}
